// === Определяем market (Web / Telegram) ===

import bridge, { parseURLSearchParamsForGetLaunchParams } from "@vkontakte/vk-bridge";
import { currentVersion } from "./constants/interests";
import { captureRawVkLaunchParams } from "@/utils/vkMiniAppLaunchParams";

// Preserve the byte-for-byte launch query before router initialization.
captureRawVkLaunchParams();

(function detectMarket() {
    try {
        const href = window.location.href.toLowerCase();
        const ua = navigator.userAgent.toLowerCase();

        if (
            ua.includes("telegram") ||
            ua.includes("tma") ||
            href.includes("tgwebapp") ||
            href.includes("telegram-web-app") ||
            (window as any).Telegram?.WebApp
        ) {
            localStorage.setItem("market", "telegram");
            console.log("[market] Telegram Mini App обнаружен ✅");
        } else {
            localStorage.setItem("market", "web");
            console.log("[market] Web режим ✅");
        }
    } catch (e) {
        console.warn("[market] detect fail:", e);
        localStorage.setItem("market", "web");
    }
})();

(function applySavedTheme() {
    try {
        const savedTheme = localStorage.getItem("theme");
        document.documentElement.classList.toggle("dark", savedTheme === "dark");
    } catch (e) {
        console.warn("[theme] failed to apply saved theme", e);
    }
})();

function detectTelegramPlatform(): "tg-mobile" | "tg-desktop" | "tg-web" | null {
    const ua = navigator.userAgent.toLowerCase();
    const hostname = window.location.hostname.toLowerCase();

    // Telegram Desktop
    if (ua.includes("tdesktop") || ua.includes("telegramdesktop")) {
        return "tg-desktop";
    }

    // Telegram Web
    if (hostname.includes("web.telegram.org") || ua.includes("web.telegram")) {
        return "tg-web";
    }

    // Telegram Mobile App (iOS/Android / WebView / MiniApp)
    if (
        ua.includes("iphone") ||
        ua.includes("ipad") ||
        ua.includes("android") ||
        ua.includes("tgminiapp") ||
        ua.includes("telegram") // любой UA Telegram
    ) {
        return "tg-mobile";
    }

    return null;
}


// Vue и основные зависимости
import { createApp, ref } from "vue";
import { createPinia } from "pinia";
import router, { markTelegramAuthChecked } from "./router";
import "vue-tel-input/vue-tel-input.css";
import "@/assets/styles/vue-tel-input.override.css";
// Плагины
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import Vue3Toastify, { ToastContainerOptions } from "vue3-toastify";
const { toast } = await import("vue3-toastify");
//@ts-ignore
import VueTelInput from "vue-tel-input";

// Стили — все критические синхронно, до mount
import "vue-tel-input/vue-tel-input.css";
import "@/assets/fonts/fonts.scss";
import "./assets/styles/main.scss";

// Компоненты
import App from "./App.vue";
import VueLazyload from "vue-lazyload";

// 🔒 Хранение токенов
const localforageModule = await import("localforage");
const localforage = localforageModule.default || localforageModule;
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";

// API/утилиты
import { preloadSearchChunk } from "@/preload/SearchPreloader";

import { createVuetify } from "vuetify";
import {VDialog,VCard,VDatePicker,VCol}  from "vuetify/components";
import * as directives from "vuetify/directives";

const vuetify = createVuetify({
    components: {
        VDialog,
        VCard,
        VDatePicker,
        VCol,
    },
    directives,
});



// Функция для загрузки Telegram SDK (остаётся как была)
function loadTelegramScript(): Promise<void> {
    return new Promise((resolve) => {
        //@ts-ignore
        if (window.Telegram?.WebApp) return resolve()

        const script = document.createElement("script");
        script.src = "https://telegram.org/js/telegram-web-app.js";
        script.onload = () => setTimeout(resolve, 100);
        document.head.appendChild(script);
    });
}

// Функция для загрузки OneSignal SDK
function loadOneSignalScript(): Promise<void> {
    return new Promise((resolve) => {
        //@ts-ignore
        if (window.OneSignal) return resolve();

        const script = document.createElement("script");
        script.src = "https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js";
        script.async = true;
        script.onload = () => {
            console.log("OneSignal SDK loaded");
            resolve();
        };
        script.onerror = () => {
            console.error("Failed to load OneSignal SDK");
            resolve();
        };
        document.head.appendChild(script);
    });
}

// ⚡ СИНХРОННОЕ определение окружения — без await
type LaunchEnv = "telegram" | "pwa" | "browser";

function detectEnvironmentSync(): LaunchEnv {
    const urlParams = new URLSearchParams(window.location.search);

    if (
        window.location.hash.includes("#tgWebAppData=") ||
        /telegram/i.test(navigator.userAgent) ||
        urlParams.get("src") === "telegramm"
    ) {
        return "telegram";
    }

    if (
        window.matchMedia('(display-mode: standalone)').matches ||
        //@ts-ignore
        window.navigator.standalone
    ) {
        return "pwa";
    }

    return "browser";
}

function isVKLaunch(): boolean {
    const launchParams = parseURLSearchParamsForGetLaunchParams(window.location.search);

    return Boolean(
        launchParams.vk_app_id ||
        launchParams.vk_user_id ||
        launchParams.vk_platform ||
        bridge.isEmbedded()
    );
}

function initializeVKBridge(): boolean {
    if (!isVKLaunch()) return false;

    try {
        //@ts-ignore
        window.vkBridge = bridge;

        bridge
            .send("VKWebAppInit")
            .then(() => {
                console.log("[vk] VK Bridge initialized");
            })
            .catch((error) => {
                console.warn("[vk] VK Bridge init failed:", error);
            });

        return true;
    } catch (error) {
        console.warn("[vk] VK Bridge init failed:", error);
        return false;
    }
}

export type LaunchEnvExtended =
    | "telegram-mobile-fullscreen"
    | "telegram-mobile-halfscreen"
    | "telegram-mobile-overlay"
    | "telegram-mobile-external-browser"
    | "telegram-desktop"
    | "telegram-web"
    | "telegram-pwa"
    | "pwa"
    | "browser";

function isTelegramOverlayMode() {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) return false;

    const notExpanded = tg.isExpanded === false;
    const shrinking = tg.viewportHeight < tg.viewportStableHeight;

    const overlayDetected =
        tg.isClosingConfirmationEnabled === true &&
        shrinking &&
        notExpanded;

    return overlayDetected;
}

function isTelegramExternalBrowser() {
    return /tgcross/i.test(navigator.userAgent);
}

function isTelegramHalfScreen() {
    const tg = (window as any).Telegram?.WebApp;
    if (!tg) return false;

    if (tg.isExpanded) return false;
    if (isTelegramOverlayMode()) return false;
    if (isTelegramExternalBrowser()) return false;

    return true;
}

function isTrulyFullscreen(): boolean {
    const safeArea = getSafeAreaInsetTop();

    const vh = window.innerHeight;
    const scr = window.screen.height;

    // fallback для iOS viewport resize
    const realVH =
        window.visualViewport?.height ?? vh;

    // full — когда высота окна почти равна высоте экрана минус safe-area
    const threshold = 40; // допуск в px

    return Math.abs((scr - safeArea) - realVH) < threshold;
}

let cachedSafeAreaTop: number | null = null;

function getSafeAreaInsetTop(): number {
    if (cachedSafeAreaTop !== null) return cachedSafeAreaTop;
    const div = document.createElement("div");
    div.style.cssText = `
        position: absolute;
        top: 0;
        height: env(safe-area-inset-top);
        width: 0;
        pointer-events: none;
        visibility: hidden;
    `;
    document.body.appendChild(div);

    const height = div.clientHeight;
    document.body.removeChild(div);
    cachedSafeAreaTop = height;
    return height;
}

export function detectEnvironmentExtended(): LaunchEnvExtended {
    const base = detectEnvironmentSync();

    if (base === "telegram") {
        const platform = detectTelegramPlatform();
        const tg = (window as any).Telegram?.WebApp;

        const fullscreenByTg = tg?.isExpanded === true;
        const halfscreenByTg = isTelegramOverlayMode();
        const fullscreenByCss = isTrulyFullscreen();
        const halfscreenByCss = isTelegramHalfScreen()

        const isFullscreen = fullscreenByTg || fullscreenByCss;
        const isHalfscreen = halfscreenByTg && !fullscreenByCss && halfscreenByCss;

        // 🔥 MOBILE
        if (platform === "tg-mobile") {
            if (isFullscreen) {
                return "telegram-mobile-fullscreen";
            }
            if (isHalfscreen) {
                return "telegram-mobile-halfscreen";
            }
            // fallback
            return fullscreenByTg
                ? "telegram-mobile-fullscreen"
                : "telegram-mobile-halfscreen";
        }

        // 🔥 DESKTOP
        if (platform === "tg-desktop") {
            return "telegram-desktop";
        }

        // 🔥 WEB
        return "telegram-web";
    }

    // PWA
    const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        //@ts-ignore
        window.navigator.standalone;

    if (standalone) return "pwa";

    return "browser";
}

async function initializeSentry(app: ReturnType<typeof createApp>) {
    const Sentry = await import("@sentry/vue");
    Sentry.init({
        app,
        dsn: "https://7658d11b9483e7713a5577611fa9f7a6@sentry.tinderone.ru/6",
    });
}

async function initializeOneSignal(): Promise<void> {
    //@ts-ignore
    if (window.launchEnv === 'telegram') {
        console.log('OneSignal disabled in Telegram environment');
        return;
    }

    try {
        await loadOneSignalScript();
        await new Promise((resolve) => setTimeout(resolve, 1000));
        //@ts-ignore
        if (!window.OneSignal) {
            console.warn("OneSignal not available after loading");
            return;
        }
        //@ts-ignore
        await window.OneSignal.init({
            appId: "aacb25ab-5f25-410b-80a4-cbb6212c52fa",
            allowLocalhostAsSecureOrigin: window.location.hostname === "localhost",
            serviceWorkerPath: "sw.js",
            serviceWorkerParam: { scope: "/" },
            promptOptions: {
                slidedown: {
                    autoPrompt: true,
                    enabled: true,
                    timeDelay: 3,
                    pageViews: 1,
                    actionMessage:
                        "Чтобы приложение работало полноценно, разрешите, пожалуйста, отправку уведомлений.",
                    acceptButtonText: "Разрешить",
                    cancelButtonText: "Не сейчас",
                },
            },
            welcomeNotification: {
                title: "Уведомления подключены!",
                message: "Теперь приложение работает в полном функционале.",
            },
            safari_web_id: "web.onesignal.auto.6761b2d6-2c9d-4cc3-8f5b-37e94b1fdadc",
        });

        console.log("OneSignal initialized successfully");
        setupOneSignalListeners();
        window.dispatchEvent(new Event("onesignal:ready"));
    } catch (error) {
        console.warn("OneSignal initialization failed:", error);
    }
}

function setupOneSignalListeners() {
    // Навигация и foreground-уведомления обрабатываются в usePushNotifications,
    //@ts-ignore
    if (!window.OneSignal) return;
    //@ts-ignore
    window.OneSignal.Notifications.addEventListener("dismiss", (event: any) => {
        console.log("[Push] Notification dismissed", event);
    });
}

// Инициализация Telegram WebApp
let telegramWasDeactivated = false;
let telegramReauthRunning = false;
let telegramLifecycleInitialized = false;

function requireTelegramReauth() {
    if (!telegramWasDeactivated || telegramReauthRunning) return;

    telegramWasDeactivated = false;
    telegramReauthRunning = true;
    markTelegramAuthChecked(false);

    void router
        .replace({ path: "/", query: { telegram_auth: Date.now().toString() } })
        .finally(() => {
            telegramReauthRunning = false;
        });
}

function initializeTelegramWebApp() {
    //@ts-ignore
    if (!window.Telegram?.WebApp) return;
    //@ts-ignore
    const telegramWebApp = window.Telegram.WebApp;

    if (!telegramLifecycleInitialized) {
        telegramLifecycleInitialized = true;

        telegramWebApp.onEvent?.("deactivated", () => {
            telegramWasDeactivated = true;
        });
        telegramWebApp.onEvent?.("activated", requireTelegramReauth);

        // Fallback для клиентов Telegram, которые не присылают lifecycle events.
        document.addEventListener("visibilitychange", () => {
            if (document.visibilityState === "hidden") {
                telegramWasDeactivated = true;
                return;
            }

            if (document.visibilityState === "visible") {
                requireTelegramReauth();
            }
        });
    }

    //@ts-ignore
    telegramWebApp.enableClosingConfirmation();
    //@ts-ignore
    telegramWebApp.expand();
    //@ts-ignore
    if (parseFloat(telegramWebApp.version) >= 7.7) {
        //@ts-ignore
        telegramWebApp.disableVerticalSwipes();
    }
    //@ts-ignore
    if (parseFloat(telegramWebApp.version) >= 8.0) {
        //@ts-ignore
        telegramWebApp.lockOrientation();
    }
}

const lastVersion = localStorage.getItem("app_version");
// Флаг предотвращает двойную перезагрузку если SW_UPDATED придёт в тот же момент
// что и version check (оба сработают на первом обновлении)
let reloadPending = false;

async function resetLocalDevServiceWorkers() {
    if (!import.meta.env.DEV && import.meta.env.VITE_PWA_DEV !== "true") return;
    if (!("serviceWorker" in navigator)) return;

    const host = window.location.hostname;
    const isLocalHost =
        host === "localhost" ||
        host === "127.0.0.1" ||
        host === "0.0.0.0" ||
        host.endsWith(".localhost");

    if (!isLocalHost) return;

    const RESET_KEY = "local_dev_sw_reset_done";

    try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const cacheKeys = "caches" in window ? await caches.keys() : [];

        if (!registrations.length && !cacheKeys.length) {
            sessionStorage.removeItem(RESET_KEY);
            return;
        }

        await Promise.all(registrations.map(registration => registration.unregister()));

        if ("caches" in window) {
            await Promise.all(cacheKeys.map(key => caches.delete(key)));
        }

        console.info("[dev] Local service workers and browser caches cleared");

        if (navigator.serviceWorker.controller && sessionStorage.getItem(RESET_KEY) !== "1") {
            sessionStorage.setItem(RESET_KEY, "1");
            window.location.reload();
        } else {
            sessionStorage.removeItem(RESET_KEY);
        }
    } catch (error) {
        console.warn("[dev] Failed to clear local service workers/caches:", error);
    }
}

// 🔥 Главный bootstrap
async function initializeApp() {
    await resetLocalDevServiceWorkers();

    // 1. СИНХРОННО определяем окружение
    const launchEnv = detectEnvironmentSync();
    const LaunchEnvExtended = detectEnvironmentExtended();
    //@ts-ignore
    window.launchEnv = launchEnv;
    //@ts-ignore
    window.LaunchEnvExtended = LaunchEnvExtended;
    //@ts-ignore
    window.tgWebApp = window.Telegram?.WebApp || null;
    //@ts-ignore
    window.isVKLaunch = initializeVKBridge();
    console.log(`🌍 launchEnv = ${launchEnv}`);


    // 2. Создаём приложение и сразу подключаем всё нужное
    const app = createApp(App);

    const pinia = createPinia();
    pinia.use(piniaPluginPersistedstate);

    // ✅ Сначала подключаем Pinia
    app.use(pinia);

    // Stores можно использовать уже сейчас
    const authStore = useAuthStore();
    const userStore = useUserStore();

    // Токен берём ТОЛЬКО из localStorage (синхронно),
    // localforage дотащим уже после монтирования
    // A Telegram launch must always be authorized with fresh initData via
    // /auth/telegram/v2. Never let a token from a previous Mini App opening
    // select the route or suppress that request.
    if (launchEnv === "telegram") {
        localStorage.removeItem("access_token");
    }

    let token = localStorage.getItem("access_token");
    if (token) {
        authStore.setToken(token);
    }

    // Плагины
    app.use(router);

    router.onError((error) => {
        const message = (error as any)?.message || "";
        if (
            message.includes("Failed to fetch dynamically imported module") ||
            message.includes("Importing a module script failed")
        ) {
            console.warn("Ошибка загрузки чанка, перезагружаем...");
            window.location.reload();
        } else {
            console.error("Ошибка маршрутизатора:", error);
        }
    });

    app.use(VueTelInput);

    app.use(Vue3Toastify, {
        autoClose: 3000,
        position: "top-center",
        style: { marginTop: "50px" },
    } as ToastContainerOptions);

    app.use(VueLazyload, {
        loading: "/tube-spinner.svg",
        error: "/error.png",
        attempt: 1,
        preLoad: 1.3,
        throttleWait: 500,
        observer: true,
        observerOptions: { rootMargin: "0px", threshold: 0.1 },
    });

    app.use(vuetify);

    // пробрасываем окружение во всё приложение
    app.provide('launchEnv', launchEnv);
    app.provide('LaunchEnvExtended', LaunchEnvExtended);

    // 3. МОНТИРУЕМ ПРИЛОЖЕНИЕ
    app.mount("#app");

    // Скрываем splash сразу после mount
    const splash = document.getElementById("splash");
    if (splash) {
        splash.classList.add("hidden");
        splash.addEventListener("transitionend", () => splash.remove(), { once: true });
    }


    // Слушаем SW_UPDATED: новый сервис-воркер активировался пока страница была открыта.
    // В этом случае version check в конце файла не сработает (currentVersion в старом JS тот же).
    // Нужно перезагрузить страницу, чтобы загрузить новый JS-бандл.
    if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', async (event) => {
            if (event.data?.type === 'SW_UPDATED' && !reloadPending) {
                // ── Защита от зацикливания ──────────────────────────────────────
                // Если SW_UPDATED приходит 3+ раз в течение 30 секунд — это петля.
                // В этом случае принудительно сбрасываем SW и кэши без тоста.
                const COUNT_KEY = 'sw_reload_count';
                const TS_KEY    = 'sw_reload_ts';
                const THRESHOLD = 3;
                const WINDOW_MS = 30_000;

                const now     = Date.now();
                const lastTs  = Number(sessionStorage.getItem(TS_KEY) || 0);
                let   count   = Number(sessionStorage.getItem(COUNT_KEY) || 0);

                if (now - lastTs > WINDOW_MS) count = 0; // давно не было — сброс счётчика

                if (count >= THRESHOLD) {
                    // Зацикливание: сбрасываем всё и выходим из петли
                    console.warn('[SW] Update loop detected — performing hard reset');
                    sessionStorage.removeItem(COUNT_KEY);
                    sessionStorage.removeItem(TS_KEY);
                    try {
                        const regs = await navigator.serviceWorker.getRegistrations();
                        await Promise.all(regs.map(r => r.unregister()));
                    } catch { /* ignore */ }
                    if ('caches' in window) {
                        const keys = await caches.keys();
                        await Promise.all(keys.map(k => caches.delete(k)));
                    }
                    window.location.reload();
                    return;
                }

                sessionStorage.setItem(COUNT_KEY, String(count + 1));
                sessionStorage.setItem(TS_KEY, String(now));
                // ────────────────────────────────────────────────────────────────

                reloadPending = true;
                toast.info("Доступна новая версия приложения. Обновляем...");
                document.body.classList.add('pointer-none');
                if ('caches' in window) {
                    caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
                }
                setTimeout(() => window.location.reload(), 1000);
            }
        });
    }


    // Настраиваем хранилище
    localforage.config({
        name: "TinderOne",
        storeName: "persistent_storage",
    });

    const initSentrySafe = () => {
        try {
            initializeSentry(app);
        } catch (e) {
            console.warn("Sentry init failed:", e);
        }
    };

    if (launchEnv !== "telegram" && !import.meta.env.VITE_PWA_DEV) {
        if ("requestIdleCallback" in window) {
            requestIdleCallback(initSentrySafe, { timeout: 2000 });
        } else {
            // fallback для Safari / WebView
            setTimeout(initSentrySafe, 0);
        }
    }

    // 4.1. Если токена нет в localStorage — подтягиваем из localforage
    if (!token && launchEnv !== "telegram") {
        try {
            const stored = await localforage.getItem<string>("access_token");
            if (stored) {
                token = stored;
                localStorage.setItem("access_token", stored);
                authStore.setToken(stored);
                console.log("🔐 Token restored from localforage");
            }
        } catch (e) {
            console.warn("Failed to restore token from localforage:", e);
        }
    }

    // 4.2. Preload SearchPage — ТЕПЕРЬ ТОЛЬКО В ФОНЕ
    if (token) {
        console.log("🚀 PRELOAD SEARCH START (background)");
        try {
            await preloadSearchChunk();
        } catch (e) {
            console.warn("⚠️ preloadSearchPage failed:", e);
        }
        console.log("✅ PRELOAD SEARCH END");
    }

    // 4.3. Telegram-specific логика — тоже после mount
    if (launchEnv === "telegram") {
        try {
            await loadTelegramScript();
            initializeTelegramWebApp();

            /* ------------------------------------------
             * 🩹 FIX: восстановление Telegram initData
             * ------------------------------------------ */
            try {
                //@ts-ignore
                const tele = window.Telegram?.WebApp;

                if (tele?.initData) {
                    const initData = tele.initData;
                    const stored = localStorage.getItem("telegram_init_data");

                    if (!stored || stored !== initData) {
                        console.log("♻️ Восстанавливаем telegram_init_data");
                        localStorage.setItem("telegram_init_data", initData);
                    }
                }
            } catch (e) {
                console.warn("Ошибка восстановления telegram_init_data:", e);
            }
        } catch (err) {
            console.warn("Ошибка проверки Telegram регистрации:", err);
        }
    } else {
        // 4.4. OneSignal — СТРОГО ПОСЛЕ mount, не блокирует UI
        if (import.meta.env.VITE_PWA_DEV === "true") {
            console.log("PWA_DEV mode: OneSignal initialization skipped");
        } else {
            initializeOneSignal()
                .catch((error) => {
                    console.warn("OneSignal init failed:", error);
                });
        }
    }

    console.log("✅ Post-mount bootstrap finished");
}

// Запуск приложения
initializeApp().catch((error) => {
    console.error("Failed to initialize app:", error);
});

if (lastVersion && lastVersion !== currentVersion && !reloadPending) {
    reloadPending = true;
    toast.info("Доступна новая версия приложения. Приложение будет доступно после обновления...");
    document.body.classList.add('pointer-none');

    if ('caches' in window) {
        caches.keys().then(keys => {
            keys.forEach(k => caches.delete(k));
        });
    }

    setTimeout(() => {
        window.location.reload();
    }, 1000);
}

localStorage.setItem("app_version", currentVersion);
